import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../api';
import type { Tarif } from '../billtoro/types';
import { fehlendeFunktion, tarifFehlerFuer, upgradeAnbieten } from '../billtoro/upgrade';
import { useToast } from '../lib/toast';
import { angebotEinstellungUrl } from './AngebotDaten';
import { PdfDialog } from './PdfDialog';
import { darf, fehlerText, NurLesen, OHNE_RECHT_EINSTELLUNGEN, TarifMarke } from './tarifHinweis';
import type { MahnlaufKurz, Overview } from './types';
import { Vorlagen, type VorlageId, type VorlageInfo } from './Vorlagen';
import './einstellungen.css';

// Einstellungen (/rechnung/einstellungen, Funktionsliste Punkte 9 und 10): Vorlage der PDFs mit Vorschau und eigenes
// Briefpapier (Vorlagen.tsx), Logo und Farbe für alle Belege mit kleiner Vorschau, E-Rechnung ein- oder ausschalten
// (Standard aus, Sam 13.10.), Auftragsbestätigung nach der Annahme über den Link (Standard an), Stand der Mahnläufe
// (eingestellt auf der eigenen Seite), eigene Nummernkreise mit der nächsten Nummer, Beispiel-PDF.

interface LogoInfo {
  mime: string;
  width: number;
  height: number;
  bytes: number;
  updatedAt: string;
}
interface LayoutInfo {
  farbe: string | null;
  farben: { text: string; linie: string } | null;
  logo: LogoInfo | null;
}
interface Kreis {
  prefix: string;
  jahr: boolean;
  stellen: number;
  start: { jahr: number; nummer: number } | null;
}
type KreisArt = 'rechnung' | 'angebot' | 'auftrag' | 'lieferschein';
// je Kreis ein Block, in dieser Reihenfolge (Name, Standard-Kürzel als Beispiel)
const KREISE: [KreisArt, string, string][] = [
  ['rechnung', 'Rechnungen', 'RE-'],
  ['angebot', 'Angebote', 'AN-'],
  ['auftrag', 'Auftragsbestätigungen', 'AB-'],
  ['lieferschein', 'Lieferscheine', 'LS-'],
];
interface KreisStand {
  letzte: { jahr: number; gesamt: number };
  seq: number;
  naechste: string;
  hinweis: string | null;
}
// Schalter „E-Rechnung erstellen“ (server erechnung-an.ts); pflichtAb: ab dann von selbst für Rechnungen an Firmen
interface ErechnungInfo {
  an: boolean;
  pflichtAb: string;
}
type NummernInfo = { jahr: number; nummern: Record<KreisArt, Kreis> } & Record<KreisArt, KreisStand>;
// Schalter „Auftragsbestätigung automatisch“ (server annahme.ts): nach der Annahme über den Link erstellen und schicken
interface AngebotInfo {
  abAuto: boolean;
}

// Standard: die bisherige Schriftfarbe der PDFs
const STANDARD = '#0f1b2d';
const VORSCHLAEGE: [string, string][] = [
  ['#1f3a5f', 'Marineblau'],
  ['#0f766e', 'Petrol'],
  ['#15803d', 'Grün'],
  ['#b91c1c', 'Rot'],
  ['#c2410c', 'Orange'],
  ['#7c3aed', 'Violett'],
  ['#334155', 'Schiefergrau'],
];

// Wie server layout.ts: Kontrast zu Weiß nach WCAG, zu helle Farben im selben Farbton abdunkeln
const kanaele = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const hexAus = (c: number[]) => `#${c.map((v) => Math.min(255, Math.max(0, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
function kontrast(c: number[]) {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 1.05 / (0.2126 * r! + 0.7152 * g! + 0.0722 * b! + 0.05);
}
function abdunkeln(hex: string, min: number) {
  const c = kanaele(hex);
  if (kontrast(c) >= min) return hex;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const m = (lo + hi) / 2;
    if (kontrast(c.map((v) => Math.floor(v * m))) >= min) lo = m;
    else hi = m;
  }
  return hexAus(c.map((v) => Math.floor(v * lo)));
}

const formatNummer = (k: Kreis, jahr: number, seq: number) => `${k.prefix}${k.jahr ? `${jahr}-` : ''}${String(seq).padStart(k.stellen, '0')}`;

export function Einstellungen({ data }: { data: Overview; reload(): Promise<void> }) {
  const id = data.firm.id;
  // Billtoro: ändern nur mit dem Recht „Einstellungen“, sonst nur ansehen
  const lesen = !darf(data.tarif, 'einstellungen');
  const [layout, setLayout] = useState<LayoutInfo | null>(null);
  const [nummern, setNummern] = useState<NummernInfo | null>(null);
  const [erechnung, setErechnung] = useState<ErechnungInfo | null>(null);
  const [angebot, setAngebot] = useState<AngebotInfo | null>(null);
  const [vorlageInfo, setVorlageInfo] = useState<VorlageInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  // gewählte Farbe (undefined: wie gespeichert, null: Standard)
  const [farbe, setFarbe] = useState<string | null | undefined>(undefined);
  // gewählte Vorlage (undefined: wie gespeichert)
  const [vorlage, setVorlage] = useState<VorlageId | undefined>(undefined);
  const [pdf, setPdf] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    Promise.all([
      api.get<LayoutInfo>(`/api/rechnungstool/${id}/einstellungen/layout`),
      api.get<NummernInfo>(`/api/rechnungstool/${id}/einstellungen/nummern`),
      api.get<ErechnungInfo>(`/api/rechnungstool/${id}/einstellungen/erechnung`),
      api.get<VorlageInfo>(`/api/rechnungstool/${id}/einstellungen/vorlage`),
      api.get<AngebotInfo>(angebotEinstellungUrl(id)),
    ])
      .then(([l, n, e, v, g]) => {
        if (!aktiv) return;
        setLayout(l);
        setNummern(n);
        setErechnung(e);
        setVorlageInfo(v);
        setAngebot(g);
      })
      .catch((err) => aktiv && setError(errorText(err)));
    return () => {
      aktiv = false;
    };
  }, [id]);

  if (error && !layout) return <div className="notice bad">{error}</div>;
  if (!layout || !nummern || !erechnung || !vorlageInfo || !angebot) return <div className="spinner" aria-label="Lädt" />;

  const gewaehlt = farbe === undefined ? layout.farbe : farbe;
  const vorlageGewaehlt = vorlage ?? vorlageInfo.vorlage;
  const beispiel = `/api/rechnungstool/${id}/einstellungen/beispiel?farbe=${encodeURIComponent(gewaehlt ?? '')}&vorlage=${vorlageGewaehlt}`;

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>Einstellungen</h1>
          <p className="muted">So sehen Ihre Rechnungen, Mahnungen und Angebote aus.</p>
        </div>
        <button type="button" className="btn" onClick={() => setPdf(beispiel)}>
          Beispiel-PDF ansehen
        </button>
      </div>
      {!data.absender.rechtsform && (
        <div className="notice warn">
          Ihre Rechtsform fehlt noch. <Link to="/rechnung/angaben">In „Meine Angaben“ ergänzen</Link>, dann stehen die Pflichtangaben
          (z. B. Registergericht und Geschäftsführer) unten auf jeder Rechnung.
        </div>
      )}
      <Vorlagen
        firmId={id}
        farbe={gewaehlt}
        wahl={vorlageGewaehlt}
        onWahl={setVorlage}
        gespeichert={vorlageInfo}
        onSaved={setVorlageInfo}
        tarif={data.tarif}
        lesen={lesen}
        postAn={data.post.an}
      />
      <Aussehen
        firmId={id}
        firma={data.absender.name}
        layout={layout}
        farbe={gewaehlt}
        naechste={nummern.rechnung.naechste}
        onFarbe={setFarbe}
        onLayout={setLayout}
        tarif={data.tarif}
        lesen={lesen}
      />
      <ERechnung firmId={id} info={erechnung} onSaved={setErechnung} lesen={lesen} />
      <AngebotEinstellung firmId={id} info={angebot} onSaved={setAngebot} lesen={lesen} einschalten={darf(data.tarif, 'angebote')} />
      <MahnlaufKarte mahnlauf={data.mahnlauf} tarif={data.tarif} />
      <Nummern firmId={id} info={nummern} onSaved={setNummern} lesen={lesen} />
      <PdfDialog url={pdf} title="Beispiel-PDF" download={pdf ? `${pdf}&download=1` : undefined} onClose={() => setPdf(null)} />
    </>
  );
}

// Logo und Farbe mit kleiner Vorschau des Briefkopfs
function Aussehen({
  firmId,
  firma,
  layout,
  farbe,
  naechste,
  onFarbe,
  onLayout,
  tarif,
  lesen = false,
}: {
  firmId: string;
  firma: string;
  layout: LayoutInfo;
  farbe: string | null;
  naechste: string;
  onFarbe(f: string | null): void;
  onLayout(l: LayoutInfo): void;
  // Billtoro: Logo und eigene Farbe erst mit Tarif (ausprobieren geht immer, speichern öffnet sonst „Mehr mit Billtoro“)
  tarif?: Tarif | null;
  lesen?: boolean;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logo = layout.logo;
  const geaendert = farbe !== layout.farbe;
  const text = farbe ? abdunkeln(farbe, 4.5) : STANDARD;
  const linie = farbe ? abdunkeln(farbe, 3) : null;
  const eigene = !!farbe && !VORSCHLAEGE.some(([v]) => v === farbe);
  // so klein, dass es auf dem PDF kleiner als die volle Größe erscheint (höchstens 120 dpi)
  const klein = logo && logo.width < 300 && logo.height < 84;
  const logoFehlt = fehlendeFunktion(tarif, 'logo');

  async function hochladen(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file, file.name);
    try {
      const l = await api.put<LogoInfo>(`/api/rechnungstool/${firmId}/logo`, fd);
      onLayout({ ...layout, logo: l });
      toast('Logo gespeichert');
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function entfernen() {
    setError(null);
    setBusy(true);
    try {
      await api.del(`/api/rechnungstool/${firmId}/logo`);
      onLayout({ ...layout, logo: null });
      toast('Logo entfernt');
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function farbeSpeichern() {
    if (farbe !== null && upgradeAnbieten(tarif, 'logo')) return;
    setError(null);
    setBusy(true);
    try {
      const l = await api.put<LayoutInfo>(`/api/rechnungstool/${firmId}/einstellungen/layout`, { farbe });
      onLayout(l);
      onFarbe(l.farbe);
      toast('Farbe gespeichert');
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rt-card rt-set" aria-labelledby="rt-set-aussehen">
      <h2 id="rt-set-aussehen">
        Logo und Farbe
        <TarifMarke tarif={tarif} fehlt={logoFehlt} />
      </h2>
      {tarif && logoFehlt && <p className="rt-hint">{tarifFehlerFuer(tarif, 'logo')?.error} Hier sehen Sie schon, wie es aussieht.</p>}
      <div className="rt-set-cols">
        <div className="rt-set-ctl">
          <NurLesen an={lesen} hinweis={OHNE_RECHT_EINSTELLUNGEN}>
            <h3>Logo</h3>
            <div className="rt-logo-row">
              <div className={`rt-logo-box${logo ? '' : ' leer'}`}>
                {logo ? <img src={`/api/rechnungstool/${firmId}/logo?v=${encodeURIComponent(logo.updatedAt)}`} alt="Ihr Logo" /> : 'Noch kein Logo'}
              </div>
              <div className="rt-logo-act">
                <button
                  type="button"
                  className={logoFehlt ? 'btn rt-gesperrt' : 'btn'}
                  onClick={() => (logoFehlt ? upgradeAnbieten(tarif, 'logo') : input.current?.click())}
                  disabled={busy || logoFehlt === 'bald'}
                >
                  {logo ? 'Anderes Logo' : 'Logo hochladen'}
                  <TarifMarke tarif={tarif} fehlt={logoFehlt} />
                </button>
                {logo && (
                  <button type="button" className="linkbtn rt-danger" onClick={() => void entfernen()} disabled={busy}>
                    Logo entfernen
                  </button>
                )}
                <input ref={input} type="file" accept="image/png,image/jpeg" hidden onChange={(e) => void hochladen(e)} aria-label="Logo-Datei" />
              </div>
            </div>
            <p className="rt-hint">PNG oder JPG, höchstens 1 MB. Am besten PNG mit durchsichtigem Hintergrund. Steht oben rechts.</p>
            {klein && (
              <p className="rt-hint">
                Ihr Logo ist recht klein ({logo.width} × {logo.height} Pixel) und erscheint deshalb kleiner, damit es scharf bleibt. Für die
                volle Größe bitte mindestens 300 Pixel breit.
              </p>
            )}

            <h3 id="rt-farbe-l">Farbe für Titel und Linien</h3>
            <div className="rt-farben" role="radiogroup" aria-labelledby="rt-farbe-l">
              <button type="button" role="radio" aria-checked={!farbe} className={`rt-farbe${!farbe ? ' on' : ''}`} onClick={() => onFarbe(null)}>
                <i style={{ background: STANDARD }} />
                Standard
              </button>
              {VORSCHLAEGE.map(([v, name]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={farbe === v}
                  aria-label={name}
                  title={name}
                  className={`rt-farbe rt-farbe-rund${farbe === v ? ' on' : ''}`}
                  onClick={() => onFarbe(v)}
                >
                  <i style={{ background: v }} />
                </button>
              ))}
              <label className={`rt-farbe${eigene ? ' on' : ''}`}>
                <input type="color" value={farbe ?? '#1f3a5f'} onChange={(e) => onFarbe(e.target.value.toLowerCase())} aria-label="Eigene Farbe" />
                Eigene
              </label>
            </div>
            {farbe && text !== farbe && (
              <p className="rt-hint">Diese Farbe ist für Schrift zu hell. Für Titel und Firmennamen nehmen wir einen dunkleren Ton, damit man sie gut lesen kann.</p>
            )}
            {error && <div className="notice bad">{error}</div>}
            {geaendert && (
              <div className="rt-actions">
                <button type="button" className="btn" onClick={() => onFarbe(layout.farbe)} disabled={busy}>
                  Zurücksetzen
                </button>
                <button
                  type="button"
                  className={logoFehlt && farbe !== null ? 'btn primary rt-gesperrt' : 'btn primary'}
                  onClick={() => void farbeSpeichern()}
                  disabled={busy || (logoFehlt === 'bald' && farbe !== null)}
                >
                  Farbe speichern
                  {farbe !== null && <TarifMarke tarif={tarif} fehlt={logoFehlt} />}
                </button>
              </div>
            )}
          </NurLesen>
        </div>

        {/* Vorschau: Kopf der Rechnung auf weißem Papier */}
        <div className="rt-mini" aria-label="Vorschau des Briefkopfs" role="img">
          <div className="rt-mini-kopf">
            <div>
              <b style={{ color: text }}>{firma}</b>
              <span className="rt-mini-z" />
            </div>
            {logo && <img src={`/api/rechnungstool/${firmId}/logo?v=${encodeURIComponent(logo.updatedAt)}`} alt="" />}
          </div>
          <span className="rt-mini-abs" style={linie ? { borderColor: linie } : undefined} />
          <b className="rt-mini-titel" style={{ color: text }}>
            Rechnung {naechste}
          </b>
          <span className="rt-mini-z" />
          <span className="rt-mini-z kurz" />
          <span className="rt-mini-fuss" style={linie ? { borderColor: linie } : undefined} />
        </div>
      </div>
    </section>
  );
}

// Nummernkreise für Rechnungen (mit Stornos und Korrekturen), Angebote, Auftragsbestätigungen und Lieferscheine: Kürzel,
// Jahr, Stellen, Startnummer
type KreisForm = { prefix: string; jahr: boolean; stellen: string; start: string };
const formOf = (k: Kreis, jahr: number): KreisForm => ({
  prefix: k.prefix,
  jahr: k.jahr,
  stellen: String(k.stellen),
  // Startnummer eines anderen Jahres gilt nicht mehr
  start: k.start && (!k.jahr || k.start.jahr === jahr) ? String(k.start.nummer) : '',
});
const formsOf = (n: NummernInfo) => Object.fromEntries(KREISE.map(([art]) => [art, formOf(n.nummern[art], n.jahr)])) as Record<KreisArt, KreisForm>;

// E-Rechnung: ein Haken, gespeichert beim Anklicken. Kunden mit XRechnung (Haken beim Kunden) bekommen sie immer.
function ERechnung({
  firmId,
  info,
  onSaved,
  lesen = false,
}: {
  firmId: string;
  info: ErechnungInfo;
  onSaved(e: ErechnungInfo): void;
  lesen?: boolean;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // der Haken folgt dem Klick gleich, bei einem Fehler springt er zurück
  const [an, setAn] = useState(info.an);
  const ab = info.pflichtAb.slice(0, 4);

  async function umschalten(neu: boolean) {
    setError(null);
    setBusy(true);
    setAn(neu);
    try {
      const e = await api.put<ErechnungInfo>(`/api/rechnungstool/${firmId}/einstellungen/erechnung`, { an: neu });
      setAn(e.an);
      onSaved(e);
      toast(neu ? 'E-Rechnung eingeschaltet' : 'E-Rechnung ausgeschaltet');
    } catch (err) {
      setAn(!neu);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rt-card rt-set" aria-labelledby="rt-set-er">
      <h2 id="rt-set-er">E-Rechnung</h2>
      <NurLesen an={lesen} hinweis={OHNE_RECHT_EINSTELLUNGEN}>
        <label className="check">
          <input type="checkbox" checked={an} disabled={busy} onChange={(e) => void umschalten(e.target.checked)} />
          <span>E-Rechnung erstellen</span>
        </label>
      </NurLesen>
      <p className="rt-hint">Ihre Rechnungen enthalten dann die E-Rechnung (XML im PDF); ab {ab} macht die App das für Rechnungen an Firmen von selbst.</p>
      {error && <div className="notice bad">{error}</div>}
    </section>
  );
}

// Mahnläufe: nur der Stand, eingestellt wird auf der Seite „Mahnläufe“
function MahnlaufKarte({ mahnlauf: m, tarif }: { mahnlauf: MahnlaufKurz | null; tarif: Tarif | null | undefined }) {
  const stand = !m?.an ? 'Aus' : m.pausiert ? 'Pausiert' : m.modus === 'senden' ? 'Automatisch senden' : 'Nur vorschlagen';
  return (
    <section className="rt-card rt-set" aria-labelledby="rt-set-ml">
      <h2 id="rt-set-ml">
        Mahnläufe <TarifMarke tarif={tarif} fehlt={fehlendeFunktion(tarif, 'mahnlauf')} />
      </h2>
      <p className="rt-set-ml">
        <span>{stand}</span>
        <Link to="/rechnung/mahnlauf">Einstellen →</Link>
      </p>
    </section>
  );
}

// Nimmt der Kunde ein Angebot über den Link an: Auftragsbestätigung erstellen und ihm schicken (Standard an).
// einschalten: Billtoro-Recht „Angebote und Aufträge“ (ohne geht nur ausschalten, der Server prüft)
function AngebotEinstellung({
  firmId,
  info,
  onSaved,
  lesen = false,
  einschalten = true,
}: {
  firmId: string;
  info: AngebotInfo;
  onSaved(g: AngebotInfo): void;
  lesen?: boolean;
  einschalten?: boolean;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // der Haken folgt dem Klick gleich, bei einem Fehler springt er zurück
  const [an, setAn] = useState(info.abAuto);

  async function umschalten(neu: boolean) {
    setError(null);
    setBusy(true);
    setAn(neu);
    try {
      const g = await api.put<AngebotInfo>(angebotEinstellungUrl(firmId), { abAuto: neu });
      setAn(g.abAuto);
      onSaved(g);
      toast(neu ? 'Automatische Auftragsbestätigung eingeschaltet' : 'Automatische Auftragsbestätigung ausgeschaltet');
    } catch (err) {
      setAn(!neu);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rt-card rt-set" aria-labelledby="rt-set-ag">
      <h2 id="rt-set-ag">Angebote</h2>
      <NurLesen an={lesen} hinweis={OHNE_RECHT_EINSTELLUNGEN}>
        <label className="check">
          <input type="checkbox" checked={an} disabled={busy || (!an && !einschalten)} onChange={(e) => void umschalten(e.target.checked)} />
          <span>Auftragsbestätigung automatisch erstellen und schicken</span>
        </label>
      </NurLesen>
      {!lesen && !einschalten && <p className="rt-hint">Einschalten nur mit dem Recht „Angebote und Aufträge“. Ausschalten geht.</p>}
      <p className="rt-hint">Nimmt Ihr Kunde ein Angebot über den Link an, erstellt die App die Auftragsbestätigung und schickt sie ihm per E-Mail.</p>
      {error && <div className="notice bad">{error}</div>}
    </section>
  );
}

function Nummern({
  firmId,
  info,
  onSaved,
  lesen = false,
}: {
  firmId: string;
  info: NummernInfo;
  onSaved(n: NummernInfo): void;
  lesen?: boolean;
}) {
  const toast = useToast();
  const [f, setF] = useState(() => formsOf(info));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const kreisAus = (k: KreisForm): Kreis => ({
    prefix: k.prefix.trim(),
    jahr: k.jahr,
    stellen: Number(k.stellen),
    start: k.start.trim() ? { jahr: info.jahr, nummer: Number(k.start.trim()) } : null,
  });
  const geaendert = JSON.stringify(f) !== JSON.stringify(formsOf(info));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (lesen) return;
    setError(null);
    setBusy(true);
    try {
      const n = await api.put<NummernInfo>(
        `/api/rechnungstool/${firmId}/einstellungen/nummern`,
        Object.fromEntries(KREISE.map(([art]) => [art, kreisAus(f[art])])),
      );
      onSaved(n);
      setF(formsOf(n));
      toast('Nummern gespeichert');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="rt-card rt-set" onSubmit={submit} noValidate aria-labelledby="rt-set-nr">
      <h2 id="rt-set-nr">Nummern</h2>
      <p className="rt-hint">
        Fortlaufend und ohne Lücke. Wechseln Sie von einem anderen Programm, tragen Sie als Startnummer die nächste freie Nummer ein.
      </p>
      <NurLesen an={lesen} hinweis={OHNE_RECHT_EINSTELLUNGEN}>
        {KREISE.map(([art, name, kuerzel]) => (
          <KreisFelder
            key={art}
            name={name}
            kuerzel={kuerzel}
            value={f[art]}
            stand={info[art]}
            jahr={info.jahr}
            kreis={kreisAus(f[art])}
            onChange={(v) => setF({ ...f, [art]: v })}
          />
        ))}
      </NurLesen>
      {error && <div className="notice bad">{error}</div>}
      {!lesen && (
        <div className="rt-actions">
          <button type="submit" className="btn primary" disabled={busy || !geaendert}>
            Nummern speichern
          </button>
        </div>
      )}
    </form>
  );
}

function KreisFelder({
  name,
  kuerzel,
  value,
  stand,
  jahr,
  kreis,
  onChange,
}: {
  name: string;
  kuerzel: string;
  value: KreisForm;
  stand: KreisStand;
  jahr: number;
  kreis: Kreis;
  onChange(v: KreisForm): void;
}) {
  const set = (k: keyof KreisForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange({ ...value, [k]: e.target.value });
  // nächste Nummer wie auf dem Server (nummern.ts): nach der letzten vergebenen, mindestens die Startnummer
  const zuletzt = kreis.jahr ? stand.letzte.jahr : stand.letzte.gesamt;
  const startOk = !!kreis.start && Number.isInteger(kreis.start.nummer) && kreis.start.nummer > 0;
  const seq = Math.max(zuletzt + 1, startOk ? kreis.start!.nummer : 0);
  const stellenOk = kreis.stellen >= 1 && kreis.stellen <= 6;
  const naechste = stellenOk ? formatNummer(kreis, jahr, seq) : '–';
  const weiter = startOk && kreis.start!.nummer <= zuletzt;
  return (
    <fieldset className="rt-kreis">
      <legend>{name}</legend>
      <div className="rt-grid">
        <label className="field">
          <span>Kürzel</span>
          <input value={value.prefix} onChange={set('prefix')} maxLength={10} autoComplete="off" placeholder={`z. B. ${kuerzel}`} />
        </label>
        <label className="field">
          <span>Stellen</span>
          <select value={value.stellen} onChange={set('stellen')}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n} ({'0'.repeat(n - 1)}1)
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Startnummer{value.jahr ? ` ${jahr}` : ''}</span>
          <input value={value.start} onChange={set('start')} inputMode="numeric" placeholder="frei lassen" />
        </label>
        <label className="check rt-kreis-jahr">
          <input type="checkbox" checked={value.jahr} onChange={(e) => onChange({ ...value, jahr: e.target.checked })} />
          <span>Jahr in der Nummer (zählt jedes Jahr ab 1)</span>
        </label>
      </div>
      <p className="rt-kreis-next">
        Nächste Nummer: <b>{naechste}</b>
      </p>
      {weiter && (
        <p className="rt-hint">
          Es sind schon Nummern bis {formatNummer(kreis, jahr, zuletzt)} vergeben. Es geht mit {naechste} weiter.
        </p>
      )}
    </fieldset>
  );
}
